import {
  CategoriesByIdsDocument,
  CategoriesByUidsDocument,
  LinkedCategoriesDocument
} from "../graphql/Category.mjs";
import { toNonNullable } from "../utils/collections.mjs";
import { useQuery } from "villus";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
export const ROOT_CATEGORY_ID = "2";
function useFetchCategoriesByIds(ids) {
  const { data } = useQuery({
    query: CategoriesByIdsDocument.toString(),
    variables: computed(() => ({ ids: ids.value }))
  });
  return {
    data
  };
}
function useFetchCategoriesByUids(ids) {
  const { data } = useQuery({
    query: CategoriesByUidsDocument.toString(),
    variables: computed(() => ({ uids: ids.value }))
  });
  return {
    data
  };
}
function useFetchLinkedCategories(whiteList, targetCategory) {
  const { data } = useQuery({
    query: LinkedCategoriesDocument.toString(),
    variables: computed(() => ({
      input: {
        ...whiteList.value.length ? { ids: { in: whiteList.value } } : {},
        ...targetCategory.value ? {
          parent_id: {
            eq: targetCategory.value.toString()
          }
        } : {}
      },
      parentCategory: targetCategory.value?.toString() || ""
    })),
    fetchOnMount: computed(() => !!targetCategory.value).value,
    cachePolicy: "network-only"
  });
  return {
    data
  };
}
export function useLinkedCategories(whiteList = computed(() => []), targetCategory = computed(() => ROOT_CATEGORY_ID), _useFetchLinkedCategories = useFetchLinkedCategories) {
  const categories = ref([]);
  const parentCategory = ref(null);
  const { data } = _useFetchLinkedCategories(whiteList, targetCategory);
  watch(data, (value) => {
    if (value) {
      categories.value = [
        ...toNonNullable(value?.categories?.items).map((c) => ({
          id: c?.id ?? 0,
          name: c?.name || "",
          url_key: c?.url_key || "",
          url: c?.url || "",
          path: c?.path?.split("/").slice(2).join("/") || "",
          // filter out the first two levels
          image: c?.image || "",
          children: c.children || [],
          level: c?.level ?? 0
        }))
      ];
      parentCategory.value = targetCategory.value ? value?.parentCategory?.items?.[0] : null;
    }
  });
  return {
    parentCategory,
    categories
  };
}
export function useCategorySlider(id, _useLinkedCategories = useLinkedCategories) {
  const category = ref(null);
  const parent = ref(id || null);
  const { t } = useI18n();
  watch(category, (v) => {
    parent.value = v?.children?.length ? v.id?.toString() || null : v?.path?.split("/").at(-2) || null;
  });
  const { categories: sliderCategories, parentCategory } = _useLinkedCategories(
    computed(() => []),
    computed(() => parent.value ?? ROOT_CATEGORY_ID)
  );
  return {
    category,
    parentCategory,
    categories: computed(
      () => [
        {
          id: parentCategory.value?.id,
          name: parentCategory.value && resolveCategoryName(t)(parentCategory.value),
          url_key: parentCategory.value?.url_key || "",
          url: parentCategory.value?.url || "",
          path: parentCategory.value?.path?.split("/").slice(2).join("/"),
          // filter out the first two levels
          image: parentCategory.value?.image || "",
          children: parentCategory.value?.children || [],
          level: parentCategory.value?.level ?? 0
        },
        ...sliderCategories.value
      ].filter((c) => c.id && c.id.toString() !== ROOT_CATEGORY_ID)
    )
  };
}
export function useGetCategoriesByIds(ids) {
  const categories = ref([]);
  const { data } = useFetchCategoriesByIds(ids);
  watch(data, (newData) => {
    categories.value = newData?.categories?.items?.map((c) => ({
      id: c?.id ?? 0,
      name: c?.name || "",
      url_key: c?.url_key || "",
      url: c?.url || "",
      path: c?.path?.split("/").slice(2).join("/"),
      // filter out the first two levels
      image: c?.image || "",
      level: c?.level ?? 0,
      children: c?.children || []
    })) || [];
  });
  return {
    categories
  };
}
export function useGetCategoriesByUids(uids) {
  const categories = ref([]);
  const { data } = useFetchCategoriesByUids(uids);
  watch(data, (newData) => {
    categories.value = newData?.categories?.items?.map((c) => ({
      id: c?.id ?? 0,
      name: c?.name || "",
      url_key: c?.url_key || "",
      url: c?.url || "",
      path: c?.path?.split("/").slice(2).join("/"),
      // filter out the first two levels
      image: c?.image || "",
      level: c?.level ?? 0,
      children: c?.children || []
    })) || [];
  });
  return {
    categories
  };
}
export function resolveCategoryName(t) {
  return (category) => {
    if (category?.id?.toString() === ROOT_CATEGORY_ID) {
      return `${t("all")} ${t("categories")}`;
    }
    return `${t("all")} ${category?.name}`;
  };
}
